/**
 * R2 and R3: the preset options rendered from the server's own `presets`
 * array, and applying one as a single `/notification-settings/apply-preset`
 * call with no client-side copy of what a preset contains.
 *
 * `saved` is local UI state, not a second copy of server data: it tracks
 * only "did the last apply succeed," cleared as soon as a new one starts,
 * the same one-slot pattern `PrivacyCard.tsx` already uses for its own
 * `SavedNote`. `error-modals`'s always-on rule requires it; a preset apply
 * with no visible confirmation is otherwise silent.
 */
import { useNotificationSettingsApplyPreset } from "@sportsmet/notification-sdk/private";
import { Radio, SavedNote, type typeRadioItem } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";

export type typePresetOption = {
  presetId: string;
  label: string;
  description: string;
};

export const PresetPicker = ({
  presets,
  activePresetId,
  onApplied,
}: {
  presets: readonly typePresetOption[];
  activePresetId: string | null;
  onApplied: () => void;
}): ReactNode => {
  const [saved, setSaved] = useState(false);

  const apply = useNotificationSettingsApplyPreset({
    onSuccess: (result) => {
      if (!showApiError(result)) setSaved(true);
      onApplied();
    },
  });

  const items: typeRadioItem[] = presets.map((p) => ({
    value: p.presetId,
    label: p.label,
    description: p.description,
  }));

  return (
    <div className="flex flex-col gap-2" data-testid="preset-picker">
      <Radio
        label="Start from a preset"
        value={activePresetId ?? undefined}
        items={items}
        disabled={apply.isPending}
        onValueChange={(value) => {
          setSaved(false);
          // The generated mutation body types `presetId` as the closed
          // three-member union ISSUE-246 shipped, while this component's own
          // props are deliberately plain `string` (R2: presets are
          // staff-editable and this component must not depend on today's
          // three ids). The cast bridges that gap at the one call site
          // rather than widening the component's types.
          apply.mutate({
            presetId: value as Parameters<typeof apply.mutate>[0]["presetId"],
          });
        }}
      />
      <p className="text-caption text-ink-secondary">
        Picking one sets every switch below. You can still change any of them
        afterward.
      </p>
      <SavedNote saved={saved} />
    </div>
  );
};
